import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import { 
  FaWhatsapp, 
  FaEnvelope, 
  FaInstagram, 
  FaArrowUp 
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const FooterSection = styled.footer`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  padding: 3rem 0 1rem;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 2fr 1fr 1fr;
  }
`;

const FooterCTA = styled.div`
  background-color: ${props => props.theme.colors.grayLight};
  color: ${props => props.theme.colors.black};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.boxShadow};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    text-align: left;
    padding: 1.25rem 1.5rem;
  }
`;

const FooterLogo = styled.div`
  font-family: ${props => props.theme.fonts.heading};
  font-weight: ${props => props.theme.fontWeights.bold};
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const FooterAbout = styled.p`
  color: ${props => props.theme.colors.grayLight};
  margin-bottom: 1.5rem;
`;

const FooterSocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.white};
  }
`;

const FooterTitle = styled.h4`
  color: ${props => props.theme.colors.white};
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
`;

const FooterNav = styled.ul`
  list-style: none;
`;

const FooterNavItem = styled.li`
  margin-bottom: 0.75rem;
`;

const FooterLink = styled.a`
  color: ${props => props.theme.colors.grayLight};
  text-decoration: none;
  transition: color 0.3s ease;
  
  text-decoration: none;
  position: relative;
  transition: all 0.3s ease;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.white};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.theme.colors.white};
    
    &:after {
      width: 100%;
    }
  }
`;

const FooterCopyright = styled.div`
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.grayMedium};
  font-size: 0.9rem;
`;

const DevLink = styled(motion.a)`
  color: ${props => props.theme.colors.white};
  text-decoration: none;
  position: relative;
  transition: all 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.white};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.theme.colors.white};
    
    &:after {
      width: 100%;
    }
  }
`;

const BackToTopButton = styled.a`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  box-shadow: ${props => props.theme.boxShadow};
  opacity: ${props => (props.$visible ? 0.9 : 0)};
  transform: translateY(${props => (props.$visible ? '0' : '8px')});
  transition: opacity 0.3s ease, transform 0.3s ease;
  pointer-events: ${props => (props.$visible ? 'auto' : 'none')};
  z-index: 99;
  
  &:hover {
    opacity: 1;
    background-color: ${props => props.theme.colors.grayMedium};
    color: ${props => props.theme.colors.primary};
  }
`;

const Footer = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const shouldShow = window.scrollY > 100;
      setShowBackToTop(prev => (prev !== shouldShow ? shouldShow : prev));
    };

    // Avalia o estado inicial caso o usuário já esteja rolado
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleBackToTop = (e) => {
    // Garante comportamento suave mesmo sem um elemento com id="top"
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FooterSection>
      <div className="container">
        <FooterContainer>
          <FooterCTA>
            <div>
              <strong>Pronto para acelerar sua certificação?</strong>
              <div style={{ color: '#666', marginTop: '0.25rem' }}>Fale com nossa equipe e receba um diagnóstico rápido.</div>
            </div>
            <Button 
              primary 
              size="lg" 
              as={motion.a} 
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => window.open('https://wa.me/5519998621928', '_blank')}
            >
              <i className="fab fa-whatsapp" style={{ marginRight: '10px' }}></i> Fale conosco
            </Button>
          </FooterCTA>
          <FooterContent>
            <div>
              <FooterLogo>LM Consulting</FooterLogo>
              <FooterAbout>
                Consultoria especializada em certificação de eletrodomésticos,
                eficiência energética e segurança elétrica.
              </FooterAbout>
              <FooterSocialLinks>
                <SocialLink aria-label="WhatsApp" href="https://wa.me/5519998621928" target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp />
                </SocialLink>
                <SocialLink aria-label="E-mail" href="mailto:lmcertsolutions@outlook.com">
                  <FaEnvelope />
                </SocialLink>
                <SocialLink aria-label="Instagram" href="https://instagram.com/lmconsulting.global" target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </SocialLink>
              </FooterSocialLinks>
            </div>
            
            <div>
              <FooterTitle>Links Rápidos</FooterTitle>
              <FooterNav>
                <FooterNavItem>
                  <FooterLink href="#quem-somos">Quem Somos</FooterLink>
                </FooterNavItem>
                <FooterNavItem>
                  <FooterLink href="#servicos">Serviços</FooterLink>
                </FooterNavItem>
                <FooterNavItem>
                  <FooterLink href="#diferenciais">Diferenciais</FooterLink>
                </FooterNavItem>
                <FooterNavItem>
                  <FooterLink href="#contato">Contato</FooterLink>
                </FooterNavItem>
              </FooterNav>
            </div>
            
            <div>
              <FooterTitle>Contato</FooterTitle>
              <FooterNav>
                <FooterNavItem>
                  <FooterLink href="https://wa.me/5519998621928" target="_blank" rel="noopener noreferrer">
                    WhatsApp: (19) 98621-9280
                  </FooterLink>
                </FooterNavItem>
                <FooterNavItem>
                  <FooterLink href="mailto:lmcertsolutions@outlook.com">
                    lmcertsolutions@outlook.com
                  </FooterLink>
                </FooterNavItem>
                <FooterNavItem>
                  <FooterLink href="https://instagram.com/lmconsulting.global" target="_blank" rel="noopener noreferrer">
                    Instagram: @lmconsulting.global
                  </FooterLink>
                </FooterNavItem>
              </FooterNav>
            </div>
          </FooterContent>
          
          <FooterCopyright>
            &copy; {new Date().getFullYear()} LM Consulting. Todos os direitos reservados.
            Desenvolvido por <DevLink 
              href="https://www.joaopedrobatistadev.com.br/" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >João Pedro Batista Dev</DevLink>.
          </FooterCopyright>
        </FooterContainer>
      </div>
      
      <BackToTopButton 
        href="#top" 
        aria-label="Voltar ao topo"
        onClick={handleBackToTop}
        $visible={showBackToTop}
        aria-hidden={!showBackToTop}
      >
        <FaArrowUp />
      </BackToTopButton>
    </FooterSection>
  );
};

export default Footer;
